import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { format, formatDistanceToNow } from "date-fns";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import {
  loadTciManagerDashboard,
  sendTciManagerMessage,
  updateCandidateNudgeThreshold,
} from "services/fde/tciManagerService";
import { loadTciHappyPathProgress } from "services/fde/tciHappyPathService";
import { REVIEW_CADENCES } from "services/fde/tciGovernanceService";
import { FDE_ROLE_LABELS } from "services/fde/fdeRegistryService";
import { HELP_VIDEO_WORKFLOWS } from "services/helpVideoService";
import { useRegisterHelpWorkflow } from "hooks/useRegisterHelpWorkflow";

function formatReviewCadenceLabel(value) {
  const match = REVIEW_CADENCES.find((row) => row.value === value);
  return match?.label || value || "—";
}

const ACTIVITY_LABELS = {
  active: "Active",
  quiet: "Quiet",
  "not-yet-certified": "Not yet certified",
};

const TciManagerDashboard = () => {
  useRegisterHelpWorkflow(HELP_VIDEO_WORKFLOWS.TCI_MANAGER_DASHBOARD);
  const navigate = useNavigate();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const { isFdeDc, loading: fdeLoading } = useFdeRole();
  const { canAccess: isOrgAdmin } = useOrgAdminAccess();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [savingTciId, setSavingTciId] = useState(null);
  const [nudgeDrafts, setNudgeDrafts] = useState({});
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageBody, setMessageBody] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [happyPath, setHappyPath] = useState(null);

  const canAccess = isFdeDc || isGlobalAdmin;

  const loadDashboard = useCallback(async () => {
    if (!user?.uid || !institutionId || !canAccess) {
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const data = await loadTciManagerDashboard({
        uid: user.uid,
        institutionId,
      });
      setDashboard(data);
      const drafts = {};
      data.tciMetrics.forEach((row) => {
        drafts[row.tciId] = String(row.candidateNudgeThreshold ?? 2);
      });
      setNudgeDrafts(drafts);
    } catch (err) {
      setDashboard(null);
      setErrorMessage(err?.message || "Failed to load TCI Manager dashboard.");
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [user?.uid, institutionId, canAccess]);

  useEffect(() => {
    if (!fdeLoading) loadDashboard();
  }, [loadDashboard, fdeLoading]);

  useEffect(() => {
    if (!institutionId || !canAccess) {
      setHappyPath(null);
      return undefined;
    }
    let cancelled = false;
    loadTciHappyPathProgress(institutionId)
      .then((row) => {
        if (!cancelled) setHappyPath(row);
      })
      .catch(() => {
        if (!cancelled) setHappyPath(null);
      });
    return () => {
      cancelled = true;
    };
  }, [institutionId, canAccess]);

  const tciMetricsById = useMemo(
    () => Object.fromEntries((dashboard?.tciMetrics || []).map((row) => [row.tciId, row])),
    [dashboard],
  );

  const handleSaveNudge = async (tciId) => {
    const metrics = tciMetricsById[tciId];
    if (!metrics) return;
    setSavingTciId(tciId);
    setErrorMessage("");
    try {
      await updateCandidateNudgeThreshold({
        institutionId,
        tciId,
        candidateNudgeThreshold: Number(nudgeDrafts[tciId] || 2),
        existingProfile: metrics.governanceProfile,
      });
      setSuccessMessage("Review cadence threshold updated.");
      await loadDashboard();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to update threshold.");
    } finally {
      setSavingTciId(null);
    }
  };

  const handleSendMessage = async () => {
    if (!messageTarget || !messageBody.trim()) return;
    setSendingMessage(true);
    setErrorMessage("");
    try {
      await sendTciManagerMessage({
        fromUid: user.uid,
        toUid: messageTarget.uid,
        institutionId,
        domainId: messageTarget.domainId,
        message: messageBody.trim(),
      });
      setMessageTarget(null);
      setMessageBody("");
      setSuccessMessage("Message sent to your team member.");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to send message.");
    } finally {
      setSendingMessage(false);
    }
  };

  if (fdeLoading || (canAccess && !institutionId) || (canAccess && !hasLoaded) || loading) {
    return <Loading page />;
  }

  if (!canAccess) {
    return (
      <Alert severity="warning">
        TCI Manager dashboard is available to TCI Manager role holders.
      </Alert>
    );
  }

  if (errorMessage && !dashboard) {
    return (
      <Box>
        <DashboardPageHeader
          title="TCI Manager"
          subtitle="Your team, your TCIs, your clinical decisions."
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  if (hasLoaded && !dashboard?.managedTciIds?.length) {
    return (
      <Box>
        <DashboardPageHeader
          title="TCI Manager"
          subtitle="Your team, your TCIs, your clinical decisions."
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          No clinical domains are assigned to you yet. Ask your institution admin to assign you
          as TCI Manager on a domain.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="TCI Manager"
        subtitle="Your team, your TCIs, and prompt candidates awaiting your review."
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {happyPath && !happyPath.setupComplete && !isOrgAdmin ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Institution setup is incomplete (domain, governance, or Clinical Annotator). Ask your ChartMind
          Manager to finish TCI setup — they have the admin checklist at{" "}
          <Typography component="span" variant="body2" fontWeight={600}>
            TCI setup
          </Typography>
          .
        </Alert>
      ) : null}

      {happyPath && happyPath.setupComplete && happyPath.nextStep && !happyPath.allComplete ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <LoadingButton size="small" onClick={() => navigate(happyPath.nextStep.href)}>
              Continue
            </LoadingButton>
          }
        >
          Next loop step: {happyPath.nextStep.title}. {happyPath.nextStep.description}
        </Alert>
      ) : null}

      <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
        Your TCIs
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {dashboard.managedTciIds.map((tciId) => {
          const metrics = tciMetricsById[tciId];
          if (!metrics) return null;
          const tciName = dashboard.tciNameById[tciId] || tciId;

          return (
            <Grid item xs={12} md={6} key={tciId}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Stack spacing={1.5}>
                  {metrics.metricsLoadFailed ? (
                    <Alert severity="error">
                      {metrics.metricsErrorMessage ||
                        "Unable to load TCI metrics. Check your permissions or try again."}
                    </Alert>
                  ) : null}
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="subtitle1">{tciName}</Typography>
                    <Chip
                      size="small"
                      color={metrics.loopHealth.tone}
                      label={metrics.loopHealth.label}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {metrics.loopHealth?.reason || ""}
                  </Typography>

                  {metrics.configuringBlocker && (
                    <Alert
                      severity="info"
                      action={
                        <LoadingButton
                          size="small"
                          onClick={() => navigate(metrics.configuringBlocker.href)}
                        >
                          Continue
                        </LoadingButton>
                      }
                    >
                      {metrics.configuringBlocker.nextAction}
                    </Alert>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    {metrics.fdeCCount}{" "}
                    {metrics.fdeCCount === 1
                      ? FDE_ROLE_LABELS["fde-c"]
                      : `${FDE_ROLE_LABELS["fde-c"]}s`}{" "}
                    on your team
                  </Typography>
                  <Typography variant="body2">
                    Pending annotations: {metrics.pendingAnnotations}
                  </Typography>
                  <Typography variant="body2">
                    Pending candidates: {metrics.pendingCandidateCount}
                  </Typography>
                  <Typography variant="body2">
                    Critical since last promotion: {metrics.criticalSinceLastReview}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last promotion:{" "}
                    {metrics.lastPromotionAt
                      ? format(metrics.lastPromotionAt, "MMM d, yyyy")
                      : "None yet"}
                  </Typography>

                  {metrics.criticalSessions?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Critical sessions
                      </Typography>
                      <Stack spacing={0.5}>
                        {metrics.criticalSessions.map((row) => (
                          <LoadingButton
                            key={row.id}
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              navigate(`/dashboard/sessions/${row.sessionId}`)
                            }
                          >
                            Open session{" "}
                            {row.createdAt
                              ? format(row.createdAt, "MMM d")
                              : row.sessionId?.slice(0, 8)}
                          </LoadingButton>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {metrics.recentDecisions?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Recent decisions
                      </Typography>
                      <Stack spacing={0.5}>
                        {metrics.recentDecisions.map((row) => (
                          <LoadingButton
                            key={row.id}
                            size="small"
                            variant="text"
                            onClick={() =>
                              navigate(`/dashboard/tci-manager/candidates/${row.id}`)
                            }
                          >
                            {row.status} —{" "}
                            {String(row.promptId || "")
                              .replace(/[-_]+/g, " ")
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase()) || "Prompt"}
                            {row.reviewedAt
                              ? ` (${format(row.reviewedAt, "MMM d")})`
                              : ""}
                          </LoadingButton>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {metrics.loopHealth?.status === "stalled" && (
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/dashboard/prompts?escalateTci=${encodeURIComponent(tciId)}`,
                        )
                      }
                    >
                      Escalate to {FDE_ROLE_LABELS["fde-t"]}
                    </LoadingButton>
                  )}

                  <LoadingButton
                    size="small"
                    variant="text"
                    onClick={() => navigate(`/dashboard/training-decks/${tciId}`)}
                  >
                    Edit Module 4 training
                  </LoadingButton>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <TextField
                      label="Remind me after this many notes"
                      type="number"
                      size="small"
                      inputProps={{ min: 1, max: 20 }}
                      value={nudgeDrafts[tciId] ?? metrics.candidateNudgeThreshold}
                      onChange={(event) =>
                        setNudgeDrafts((prev) => ({
                          ...prev,
                          [tciId]: event.target.value,
                        }))
                      }
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      loading={savingTciId === tciId}
                      onClick={() => handleSaveNudge(tciId)}
                      sx={{ flexShrink: 0 }}
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Review cadence: {formatReviewCadenceLabel(metrics.reviewCadence)}
                  </Typography>

                  {metrics.pendingCandidates.length > 0 && (
                    <Stack spacing={1}>
                      {metrics.pendingCandidates.map((candidate) => (
                        <LoadingButton
                          key={candidate.id}
                          variant="contained"
                          onClick={() =>
                            navigate(`/dashboard/tci-manager/candidates/${candidate.id}`)
                          }
                        >
                          Review candidate —{" "}
                          {String(candidate.promptId || "")
                            .replace(/[-_]+/g, " ")
                            .toLowerCase()
                            .replace(/\b\w/g, (c) => c.toUpperCase()) || "Prompt"}
                        </LoadingButton>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="h6" gutterBottom>
        My team
      </Typography>
      <Paper sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>TCI</TableCell>
              <TableCell>Training</TableCell>
              <TableCell>Annotations (30d)</TableCell>
              <TableCell>Last annotation</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dashboard.team.map((member) => (
              <TableRow
                key={member.id}
                sx={member.isQuiet ? { bgcolor: "action.hover" } : undefined}
              >
                <TableCell>{member.displayName}</TableCell>
                <TableCell>{member.tciId || "—"}</TableCell>
                <TableCell>
                  {member.certifiedAt || member.trainingComplete ? "Certified" : "Pending"}
                </TableCell>
                <TableCell>{member.annotationCount30d}</TableCell>
                <TableCell>
                  {member.lastAnnotationDate
                    ? formatDistanceToNow(member.lastAnnotationDate, { addSuffix: true })
                    : "Never"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={
                      member.activityStatus === "quiet"
                        ? "warning"
                        : member.activityStatus === "not-yet-certified"
                          ? "default"
                          : "success"
                    }
                    label={ACTIVITY_LABELS[member.activityStatus] || member.activityStatus}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <LoadingButton
                      size="small"
                      onClick={() =>
                        navigate(`/dashboard/tci-manager/team/${member.uid}`)
                      }
                    >
                      History
                    </LoadingButton>
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      onClick={() => setMessageTarget(member)}
                    >
                      Message
                    </LoadingButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!dashboard.team?.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" variant="body2">
                    No {FDE_ROLE_LABELS["fde-c"]} assignments in your domains yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={Boolean(messageTarget)}
        onClose={() => setMessageTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Message {messageTarget?.displayName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Your message"
            value={messageBody}
            onChange={(event) => setMessageBody(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setMessageTarget(null)}>Cancel</LoadingButton>
          <LoadingButton
            variant="contained"
            loading={sendingMessage}
            disabled={!messageBody.trim()}
            onClick={handleSendMessage}
          >
            Send
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TciManagerDashboard;
